import React from 'react';
import {Card, Table, Loader, Icon, Popup, Tab} from 'semantic-ui-react';
import {TrackingModal} from './trackingModal';
import {InvoiceDetailsModal} from './invoiceDetailsModal/invoiceDetailsModal';
import moment from 'moment';
import {reverseShortDate, dateFormatShow, noTimeZoneShortISODate} from '../../../common/constants/datesFormat';
import BigCalendar from '../../../common/components/calendar';
import {VIEWS} from './utils';

import './dashboard.scss';

const getDateDifference = (estimated, real) => {
  const date1 = new Date(estimated);
  const date2 = new Date(real);
  const diffTime = date1 - date2;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getHighlightedCellStyle = (cell, estimated, real) => {
  const style = {
    'background': 'rgb(16, 204, 82)',
    'padding': `${cell === 'cal' ? '5px' : '1em 0.5em'}`,
    'borderRadius': '0.75em',
    'fontWeight': 'bolder',
    'boxShadow': '1px 1px 4px grey',
    'cursor': 'pointer',
  };
  const estimatedVsReal = getDateDifference(estimated, real);
  const today = moment(new Date()).format(noTimeZoneShortISODate);
  const estimatedVsToday = getDateDifference(estimated, today);
  return real ?
    estimatedVsReal >= 0 ?
      style : {...style, 'background': '#EB0A1E'} :
    estimatedVsToday >= 0 ?
      {...style, 'background': 'yellow', 'color': 'black'} : {...style, 'background': '#EB0A1E'};
};

const getEtaPopupContent = (cell, partsImportsInvoice) => {
  const label = cell === 'eta' ? 'ETA' : 'ETA CD';
  const estimated = cell === 'eta' ? partsImportsInvoice.etaEstimated : partsImportsInvoice.etacdEstimated;
  const real = cell === 'eta' ? partsImportsInvoice.etaReal : partsImportsInvoice.etacdReal;
  const difference = getDateDifference(estimated, real);
  return <>
    <div className='flex'>
      <div><b>{label} Estimada:</b></div>
      <div style={{'marginLeft': '5px'}}>{moment(estimated).format(dateFormatShow)}</div>
    </div>
    <div className='flex'>
      <div><b>{label} Real:</b></div>
      <div style={{'marginLeft': '5px'}}>{moment(real).format(dateFormatShow)}</div>
    </div>
    <div className='flex'>
      <div><b>Diferencia:</b></div>
      <div style={{'marginLeft': '5px'}}>{difference}</div>
    </div>
  </>;
};

const getPartializedPopupContent = (partsImportsInvoice) => {
  return (
    <Table striped className='dashboard-table'>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell style={{'padding': '5px'}}></Table.HeaderCell>
          <Table.HeaderCell colSpan='2' style={{'padding': '5px'}}>ETA CD</Table.HeaderCell>
        </Table.Row>
        <Table.Row>
          <Table.HeaderCell style={{'padding': '5px'}}>Parcial</Table.HeaderCell>
          <Table.HeaderCell style={{'padding': '5px'}}>Estimada</Table.HeaderCell>
          <Table.HeaderCell style={{'padding': '5px'}}>Real</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {partsImportsInvoice.partialsData.map((x) => {
          return (
            <Table.Row>
              <Table.Cell>{x.partial}</Table.Cell>
              <Table.Cell>{moment(x.etacd_estimated).format(dateFormatShow)}</Table.Cell>
              <Table.Cell>{x.etacd_real ? moment(x.etacd_real).format(dateFormatShow) : '-'}</Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

const DataTable = ({partsImportsInvoices, loading, search, view}) => {
  return (
    <Card raised fluid>
      {
        loading ?
          <Table>
            <Table.Body>
              <Table.Row className='loader'>
                <Table.Cell colSpan='100%'>
                  <Loader active inline='centered' />
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table> :
          <Table striped className='dashboard-table'>
            <Table.Header className='orders-table-header'>
              <Table.Row>
                <Table.HeaderCell>Nº Factura</Table.HeaderCell>
                <Table.HeaderCell>Tipo Factura</Table.HeaderCell>
                <Table.HeaderCell>Vía</Table.HeaderCell>
                <Table.HeaderCell>Fecha Emisión</Table.HeaderCell>
                <Table.HeaderCell>Fecha Embarque</Table.HeaderCell>
                <Table.HeaderCell>Estado</Table.HeaderCell>
                <Table.HeaderCell>Ubicación</Table.HeaderCell>
                <Table.HeaderCell>Arribo EC</Table.HeaderCell>
                <Table.HeaderCell>Arribo CD</Table.HeaderCell>
                <Table.HeaderCell>Parcializado</Table.HeaderCell>
                <Table.HeaderCell>Tracking</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {
                partsImportsInvoices.map((partsImportsInvoice, index) => {
                  return <Table.Row key={`table-row-${index}`}>
                    <Table.Cell>
                      <InvoiceDetailsModal partImportInvoiceCode={partsImportsInvoice.invoiceNumber}
                        partialInvoiceCode={partsImportsInvoice.partial} />
                    </Table.Cell>
                    <Table.Cell>{partsImportsInvoice.orderType}</Table.Cell>
                    <Table.Cell>{partsImportsInvoice.transport === 'MARÍTIMO' ?
                      <Icon name='ship' size='huge' style={{'color': '#959595'}} /> :
                      <Icon name='flight' size='huge' style={{'color': '#959595'}} />}
                    </Table.Cell>
                    <Table.Cell>{moment(partsImportsInvoice.date).format(reverseShortDate)}</Table.Cell>
                    <Table.Cell>{moment(partsImportsInvoice.boardingDate).format(reverseShortDate)}</Table.Cell>
                    <Table.Cell>{partsImportsInvoice.statusName}</Table.Cell>
                    <Table.Cell>{partsImportsInvoice.location}</Table.Cell>
                    <Table.Cell>
                      {partsImportsInvoice.etaReal ?
                        <Popup inverted pinned on='click' content={getEtaPopupContent('eta', partsImportsInvoice)}
                          trigger={
                            <div style={getHighlightedCellStyle(
                              'eta', partsImportsInvoice.etaEstimated, partsImportsInvoice.etaReal)}>
                              {(partsImportsInvoice?.eta &&
                                moment(partsImportsInvoice.eta).format(reverseShortDate)) || 'N/A'}
                            </div>
                          }
                        /> :
                        <div style={getHighlightedCellStyle(
                          'eta', partsImportsInvoice.etaEstimated, partsImportsInvoice.etaReal)}>
                          {(partsImportsInvoice?.eta &&
                            moment(partsImportsInvoice.eta).format(reverseShortDate)) || 'N/A'}
                        </div>
                      }
                    </Table.Cell>
                    <Table.Cell>
                      { partsImportsInvoice.partialized === 'S' && view !== VIEWS.cd ?
                        <Popup wide
                          trigger={<Icon name='eye' size='big' link />}
                          on='click'
                          pinned
                          content={getPartializedPopupContent(partsImportsInvoice)}
                          inverted
                        /> :
                        partsImportsInvoice.etacdReal ?
                          <Popup
                            trigger={
                              <div style={getHighlightedCellStyle(
                                'etacd', partsImportsInvoice.etacdEstimated, partsImportsInvoice.etacdReal)}>
                                {(partsImportsInvoice?.etacd &&
                                    moment(partsImportsInvoice.etacd).format(reverseShortDate)) || 'N/A'}
                              </div>
                            }
                            on='click'
                            pinned
                            content={getEtaPopupContent('etacd', partsImportsInvoice)}
                            inverted
                          /> :
                          <div style={getHighlightedCellStyle(
                            'etacd', partsImportsInvoice.etacdEstimated, partsImportsInvoice.etacdReal)}>
                            {(partsImportsInvoice?.etacd &&
                                    moment(partsImportsInvoice.etacd).format(reverseShortDate)) || 'N/A'}
                          </div>
                      }
                    </Table.Cell>
                    <Table.Cell>{partsImportsInvoice.partialized}</Table.Cell>
                    <Table.Cell>
                      <TrackingModal invoice={partsImportsInvoice.invoiceNumber} search={search} key={index} />
                    </Table.Cell>
                  </Table.Row>;
                })
              }
            </Table.Body>
          </Table>
      }
    </Card>
  );
};

const bigCalendarFormatEvents = (data) => {
  return data.map((element, i) => {
    return {
      'id': i,
      'title': element.code,
      'start': moment(element.etacdReal || element.etacdEstimated).toDate(),
      'end': moment(element.etacdReal || element.etacdEstimated).toDate(),
      'data': element,
    };
  });
};

const bigCalendarFormatComponents = {
  'event': (props) => {
    const titleIcon = props.event?.data?.transport === 'MARÍTIMO' ?
      <Icon name='ship' size='big' /> :
      <Icon name='flight' size='big' />;
    const customStyles =
      getHighlightedCellStyle('cal', props.event?.data?.etacdEstimated, props.event?.data?.etacdReal);
    return (<>
      <div className='flex middle' color='#fff' style={customStyles}>
        {titleIcon}
        <div>
          <div className='flex top' style={{'fontWeight': 'bolder'}}>
            <div class='flex grow' style={{'textWrap': 'wrap'}}>{props.title}</div>
            <div style={{'padding': '0 6px', 'borderRadius': '50%',
              'background': 'lightgrey', 'float': 'right'}}>
              {props.event?.data?.etacdReal ? 'R' : 'E'}
            </div>
          </div>
          <div style={{'fontSize': '11px'}}>{props.event?.data?.statusName}</div>
        </div>
        
      </div>
    </>
    );
  },
};

export const DashboardTable = ({partsImportsInvoices, loading, search, view, onTabChange}) => {
  if (view === VIEWS.cd) {
    return (<Tab onTabChange={(event, data) => onTabChange(event, data)} panes={[
      {'menuItem': 'CALENDARIO', 'render': () => (<Tab.Pane>
        <BigCalendar eventsList={bigCalendarFormatEvents(partsImportsInvoices)}
          componentsList={bigCalendarFormatComponents} />
      </Tab.Pane>)},
      {'menuItem': 'PEDIDOS', 'render': () => (<Tab.Pane>
        <DataTable loading={loading} search={search} view={view}
          partsImportsInvoices={partsImportsInvoices.filter((x) => x.partialized !== 'S' ||
          (x.partialized === 'S' && x.partial !== null))} />
      </Tab.Pane>
      )},
      {'menuItem': 'CARGA RECIBIDA', 'render': () => (<Tab.Pane>
        <DataTable partsImportsInvoices={partsImportsInvoices} loading={loading} search={search} view={view} />
      </Tab.Pane>
      )},
    ]} />
    );
  }
  return (
    <DataTable partsImportsInvoices={partsImportsInvoices} loading={loading} search={search} />
  );
};
